/*
  KG gift note (Smart Cart). Owns the behaviour of the gift-note block that
  Rebuy renders from a Custom Code Block in the cart drawer (markup canon:
  Vault/tools-and-skills/skills/rebuy-master/references/kg-smart-cart-code.md).

  The note is written to the Shopify cart attribute `gift_note`, so it lands
  on the order as a custom attribute. Downstream: Shopify Flow tags the order
  `gift-order` when the attribute is non-empty, and Yuzu prints the card from
  the same attribute. Keep the key stable; three systems read it.

  The drawer is a Vue app that can rebuild its DOM on any cart change, so
  nothing here holds a reference to the block. Every event is delegated at
  document level and the typed value is re-applied to whatever block exists
  after each Rebuy cart event.
*/
(function () {
  'use strict';

  if (window.kgGiftNoteBound) return;
  window.kgGiftNoteBound = true;

  var ATTRIBUTE_KEY = 'gift_note';
  var INTENT_KEY = 'kg_gift_note_intent'; // sessionStorage; set by the PDP tick box (gift-note-option)
  var INTENT_SHOWN_KEY = 'kg_gift_note_intent_shown'; // the one-time open has happened
  var SAVE_DEBOUNCE_MS = 600;
  var STATUS_CLEAR_MS = 2500;
  var ROOT_SELECTOR = '[data-kg-gift-note]';

  // The printed card (Yuzu gift-note-card-v1, 14pt Ovo) holds 10 lines of about
  // 60 characters; measured on the template 31 Oct 2026. Characters alone can
  // never overflow it (300 wrap to 6 lines), line breaks can, so both are
  // capped here. CHARS_PER_LINE sits under the measured width so the estimate
  // counts a line too many, never too few.
  var MAX_CHARS = 300; // mirrors the textarea maxlength
  var MAX_LINES = 10; // blank lines included
  var CHARS_PER_LINE = 52;
  var LIMIT_MESSAGE = 'The card is full.';

  var draft = null;      // text typed since the last hydrate; survives a drawer re-render
  var savedValue = null; // last value confirmed written to the cart; null until first read
  var initialRead = null; // one shared /cart.js read; Rebuy fires several events at start-up
  var saveTimer = null;
  var writeChain = Promise.resolve();
  var statusTimer = null;

  function root() { return document.querySelector(ROOT_SELECTOR); }
  function field(block) { return block ? block.querySelector('[data-kg-gift-note-field]') : null; }
  function isField(target) { return !!(target && target.matches && target.matches('[data-kg-gift-note-field]')); }

  // The PDP tick box is a commitment device: ticking it on the product page
  // opens the note panel when the drawer next shows. sessionStorage can throw
  // (private mode, blocked storage); a missing flag is the safe reading.
  function readFlag(key) {
    try { return sessionStorage.getItem(key) === '1'; } catch (e) { return false; }
  }
  function writeFlag(key, on) {
    try { on ? sessionStorage.setItem(key, '1') : sessionStorage.removeItem(key); } catch (e) {}
  }
  function hasIntent() { return readFlag(INTENT_KEY); }
  // Ticking (again) re-arms the one-time open; unticking clears both flags.
  function setIntent(on) {
    writeFlag(INTENT_KEY, on);
    writeFlag(INTENT_SHOWN_KEY, false);
  }
  window.kgGiftNoteSetIntent = setIntent;
  window.kgGiftNoteHasIntent = hasIntent;

  // --- Cart I/O ---------------------------------------------------------------

  function readCartAttribute() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (cart) { return (cart.attributes && cart.attributes[ATTRIBUTE_KEY]) || ''; });
  }

  // keepalive so a save in flight survives the jump to /checkout.
  function writeCartAttribute(value) {
    var attributes = {};
    attributes[ATTRIBUTE_KEY] = value;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      keepalive: true
    }).then(function (response) {
      if (!response.ok) throw new Error('gift note: /cart/update.js ' + response.status);
      return response.json();
    });
  }

  // --- Card limit ---------------------------------------------------------------

  function estimateCardLines(text) {
    return text.split('\n').reduce(function (lines, paragraph) {
      return lines + Math.max(1, Math.ceil(paragraph.length / CHARS_PER_LINE));
    }, 0);
  }

  function fitsCard(text) {
    return text.length <= MAX_CHARS && estimateCardLines(text) <= MAX_LINES;
  }

  // Longest prefix of `insertion` that still fits once it replaces value[start, end).
  function fitInsertion(value, start, end, insertion) {
    var before = value.slice(0, start);
    var after = value.slice(end);
    var kept = insertion;
    while (kept.length && !fitsCard(before + kept + after)) kept = kept.slice(0, -1);
    return kept;
  }

  // Fallback for input that bypassed beforeinput (IME composition, old browsers).
  function truncateToCard(input) {
    var value = input.value;
    while (value.length && !fitsCard(value)) value = value.slice(0, -1);
    if (value !== input.value) input.value = value;
  }

  function insertionText(event) {
    if (event.inputType === 'insertLineBreak' || event.inputType === 'insertParagraph') return '\n';
    if (event.inputType === 'insertCompositionText') return null; // cancelling mid-composition breaks IMEs
    if (event.data !== null && event.data !== undefined) return event.data;
    if (event.dataTransfer) return event.dataTransfer.getData('text/plain');
    return null;
  }

  function showLimit(input) {
    var block = input.closest(ROOT_SELECTOR);
    if (block) renderStatus(block, LIMIT_MESSAGE);
  }

  // --- Rendering --------------------------------------------------------------

  function setState(block, open) {
    var toggle = block.querySelector('[data-kg-gift-note-toggle]');
    var panel = block.querySelector('[data-kg-gift-note-panel]');
    block.setAttribute('data-state', open ? 'open' : 'closed');
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (panel) panel.hidden = !open;
  }

  function renderStatus(block, text) {
    var status = block.querySelector('[data-kg-gift-note-status]');
    if (!status) return;
    status.textContent = text;
    clearTimeout(statusTimer);
    if (text) statusTimer = setTimeout(function () { status.textContent = ''; }, STATUS_CLEAR_MS);
  }

  // Apply the known value to whatever block currently exists.
  function hydrate() {
    var block = root();
    if (!block) return;
    var input = field(block);
    if (!input) return;
    var value = draft !== null ? draft : (savedValue || '');
    if (document.activeElement !== input && input.value !== value) input.value = value;
    if (value && block.getAttribute('data-state') !== 'open') setState(block, true);
  }

  // Opens once per tick, on the first drawer show after it, so a customer who
  // closes the panel is not re-opened on every cart change afterwards.
  function openFromIntent() {
    if (!hasIntent() || readFlag(INTENT_SHOWN_KEY)) return;
    var block = root();
    if (!block) return;
    setState(block, true);
    writeFlag(INTENT_SHOWN_KEY, true);
  }

  // The block only exists once Rebuy has rendered the drawer, so a page with
  // no block yet costs no cart read. A save that lands before the first read
  // resolves wins: the read is then stale and is dropped.
  function hydrateFromCart() {
    if (!root()) return;
    if (savedValue !== null) { hydrate(); return; }
    if (!initialRead) {
      initialRead = readCartAttribute()
        .then(function (value) {
          if (savedValue === null) savedValue = value;
          hydrate();
        })
        .catch(function (error) { console.error(error); });
    }
  }

  // --- Saving -----------------------------------------------------------------

  function flushSave() {
    clearTimeout(saveTimer);
    saveTimer = null;
    if (draft === null || draft === savedValue) return;
    var value = draft;
    writeChain = writeChain
      .then(function () { return writeCartAttribute(value); })
      .then(function () {
        savedValue = value;
        if (draft === value) draft = null;
        var block = root();
        if (block) renderStatus(block, value ? 'Saved to your basket' : 'Removed');
      })
      .catch(function (error) {
        console.error(error);
        var block = root();
        if (block) renderStatus(block, 'Not saved. Please try again.');
      });
  }

  function scheduleSave() {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(flushSave, SAVE_DEBOUNCE_MS);
  }

  // --- Events (delegated: the block can be re-rendered under us) ----------------

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest && event.target.closest('[data-kg-gift-note-toggle]');
    if (!toggle) return;
    var block = toggle.closest(ROOT_SELECTOR);
    if (!block) return;
    var open = block.getAttribute('data-state') !== 'open';
    setState(block, open);
    if (open) {
      var input = field(block);
      if (input) input.focus();
    }
  });

  // Runs before the browser applies the edit, so an insertion that would not fit
  // the card is cut to what does (or dropped) and the customer is told once.
  document.addEventListener('beforeinput', function (event) {
    var input = event.target;
    if (!isField(input) || !/^insert/.test(event.inputType || '')) return; // deletions and undo always pass
    var insertion = insertionText(event);
    if (insertion === null) return;
    var start = input.selectionStart;
    var end = input.selectionEnd;
    var kept = fitInsertion(input.value, start, end, insertion);
    if (kept === insertion) return;
    event.preventDefault();
    if (kept) {
      input.setRangeText(kept, start, end, 'end');
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    showLimit(input);
  });

  document.addEventListener('input', function (event) {
    var input = event.target;
    if (!isField(input)) return;
    if (!fitsCard(input.value)) {
      truncateToCard(input);
      showLimit(input);
    }
    draft = input.value.trim();
    scheduleSave();
  });

  document.addEventListener('focusout', function (event) {
    if (!isField(event.target)) return;
    flushSave();
  });

  // A drawer closed on Escape removes the focused field without a reliable
  // focusout; if the customer then leaves the page inside the debounce window
  // this is the last chance to write. keepalive carries it through the unload.
  window.addEventListener('pagehide', flushSave);

  ['rebuy:cart.ready', 'rebuy:cart.change', 'rebuy:smartcart.show', 'rebuy:smartcart.ready']
    .forEach(function (name) { document.addEventListener(name, hydrateFromCart); });
  document.addEventListener('rebuy:smartcart.show', openFromIntent);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateFromCart);
  } else {
    hydrateFromCart();
  }
})();
